'use client';
import { useRouteContext } from '../../hooks/useRouteContext';
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getOrderById, cancelOrder, updateOrder } from '../../redux/actions/orderActions';
import fileService from '../../services/fileService';
import FileViewer from '../../components/viewers/FileViewer';
import { formatDateFR } from '../../utils/dateFormatter';

const OrderDetails = () => {
  const { getContextualPath } = useRouteContext();

  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { order, loading, error } = useSelector(state => state.orders);
  const { user } = useSelector(state => state.auth);

  const [activeFileId, setActiveFileId] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [downloadingIndividual, setDownloadingIndividual] = useState(false);
  const [zipProgress, setZipProgress] = useState(null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [adminNotes, setAdminNotes] = useState('');

  const handleBackToList = () => navigate(getContextualPath('orders'));

  useEffect(() => {
    dispatch(getOrderById(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (order?.submission) {
      if (order.submission.mainFile) {
        setActiveFileId(order.submission.mainFile.id);
      } else if (order.submission.files?.length > 0) {
        setActiveFileId(order.submission.files[0].id);
      }
    }
  }, [order]);

  const handleDownloadAll = async () => {
    try {
      setDownloadingAll(true);
      setZipProgress({ loaded: 0 });
      await fileService.downloadOrderFiles(order.id, (loaded) => {
        setZipProgress({ loaded });
      });
    } catch (error) {
      console.error('Erreur lors du téléchargement:', error);
    } finally {
      setDownloadingAll(false);
      setZipProgress(null);
    }
  };

  const handleDownloadAllIndividual = async () => {
    try {
      setDownloadingIndividual(true);
      await fileService.downloadAllIndividually(order.submission.files);
    } catch (error) {
      console.error('Erreur lors du téléchargement:', error);
    } finally {
      setDownloadingIndividual(false);
    }
  };

  const handleCancelOrder = async () => {
    try {
      await dispatch(cancelOrder(id));
      setShowCancelModal(false);
    } catch (error) {
      console.error('Erreur lors de l\'annulation:', error);
    }
  };

  const handleOpenUpdateModal = (status) => {
    setNewStatus(status);
    setAdminNotes('');
    setShowUpdateModal(true);
  };

  const handleUpdateStatus = async () => {
    try {
      await dispatch(updateOrder(order.id, { status: newStatus, adminNotes }));
      setShowUpdateModal(false);
      dispatch(getOrderById(id));
    } catch (error) {
      console.error('Erreur lors de la mise à jour du statut:', error);
    }
  };

  const handleDownload = async (fileId, filename) => {
    if (!fileId) return;
    try {
      setDownloading(fileId);
      const { blob, filename: serverFilename } = await fileService.downloadFile(fileId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = serverFilename || filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur lors du téléchargement:', error);
    } finally {
      setDownloading(false);
    }
  };

  const formatStatus = (status) => {
    switch (status) {
      case 'pending': return 'En attente';
      case 'accepted': return 'Accepté';
      case 'in_progress': return 'En cours';
      case 'completed': return 'Terminé';
      case 'sent': return 'Expédié';
      case 'cancelled': return 'Annulé';
      default: return status;
    }
  };

  const formatNewStatus = (s) => {
    switch (s) {
      case 'accepted': return 'Acceptée';
      case 'in_progress': return 'En cours';
      case 'completed': return 'Terminée';
      case 'sent': return 'Expédiée';
      case 'cancelled': return 'Annulée';
      default: return s;
    }
  };

  const getFileIcon = (filename) => {
    if (!filename) return 'insert_drive_file';
    const ext = filename.split('.').pop().toLowerCase();
    switch (ext) {
      case 'stl':
      case 'ply': return 'view_in_ar';
      case 'cam': return 'precision_manufacturing';
      case 'pdf': return 'picture_as_pdf';
      case 'dcm': return 'medical_services';
      default: return 'insert_drive_file';
    }
  };

  // Timeline steps config
  const timelineSteps = [
    { key: 'created',     icon: 'receipt',         label: 'Commande créée',      activeStatuses: ['pending','accepted','in_progress','completed','sent','cancelled'] },
    { key: 'accepted',    icon: 'thumb_up',         label: 'Acceptée',            activeStatuses: ['accepted','in_progress','completed','sent'] },
    { key: 'in_progress', icon: 'engineering',      label: 'En cours',            activeStatuses: ['in_progress','completed','sent'] },
    { key: 'completed',   icon: 'check_circle',     label: 'Terminée',            activeStatuses: ['completed','sent'] },
    { key: 'sent',        icon: 'local_shipping',   label: 'Expédiée',            activeStatuses: ['sent'] },
  ];

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Chargement des détails...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="alert alert-danger">{error}</div>
        <button className="btn btn-primary" onClick={handleBackToList}>Retour à la liste</button>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="not-found-container">
        <i className="material-icons">error</i>
        <h2>Commande non trouvée</h2>
        <p>La commande que vous recherchez n'existe pas ou a été supprimée.</p>
        <button className="btn btn-primary" onClick={handleBackToList}>Retour à la liste</button>
      </div>
    );
  }

  const isAdmin = user?.role === 'admin';
  const canCancel = ['pending', 'accepted', 'in_progress'].includes(order.status) && !isAdmin;
  const activeFile = order.submission?.files?.find(f => f.id === activeFileId);

  return (
    <div className="order-details-container">

      {/* ── Header ── */}
      <div className="page-header">
        <div className="header-left">
          <button className="btn-icon" onClick={handleBackToList}>
            <i className="material-icons">arrow_back</i>
          </button>
          <h1 className="sd-page-title">
            Commande
            <span className="sd-page-title__user"> — {order.submission?.name}</span>
            <span className="sd-page-title__id"> #{order.id}</span>
          </h1>
        </div>
        <div className="header-actions">
          {canCancel && (
            <button className="btn btn-outline-danger" onClick={() => setShowCancelModal(true)}>
              <i className="material-icons">cancel</i>
              Annuler la commande
            </button>
          )}
        </div>
      </div>

      {/* ── Meta bar ── */}
      <div className="sd-meta-bar">
        <span className={`status-badge ${order.status}`}>{formatStatus(order.status)}</span>
        <span className="sd-meta-bar__date">Créée le {formatDateFR(order.createdAt)}</span>
        <span className="sd-meta-bar__id"># ID : {order.id}</span>
      </div>

      {/* ── Admin actions bar ── */}
      {isAdmin && !['completed', 'sent', 'cancelled'].includes(order.status) && (
        <div className="sd-admin-bar">
          <span className="sd-admin-bar__label">Actions administrateur</span>
          <div className="sd-admin-bar__actions">
            {order.status === 'pending' && (
              <button className="btn btn-success" onClick={() => handleOpenUpdateModal('accepted')}>
                <i className="material-icons">check</i>
                Accepter
              </button>
            )}
            {order.status === 'accepted' && (
              <button className="btn btn-primary" onClick={() => handleOpenUpdateModal('in_progress')}>
                <i className="material-icons">engineering</i>
                Marquer en cours
              </button>
            )}
            {order.status === 'in_progress' && (
              <button className="btn btn-success" onClick={() => handleOpenUpdateModal('completed')}>
                <i className="material-icons">check_circle</i>
                Marquer terminée
              </button>
            )}
            <button className="btn btn-outline-danger" onClick={() => handleOpenUpdateModal('cancelled')}>
              <i className="material-icons">cancel</i>
              Annuler
            </button>
          </div>
        </div>
      )}
      {isAdmin && order.status === 'completed' && (
        <div className="sd-admin-bar">
          <span className="sd-admin-bar__label">Actions administrateur</span>
          <div className="sd-admin-bar__actions">
            <button className="btn btn-success" onClick={() => handleOpenUpdateModal('sent')}>
              <i className="material-icons">local_shipping</i>
              Marquer expédiée
            </button>
          </div>
        </div>
      )}

      {/* ── Main layout ── */}
      <div className="sd-layout">

        {/* Left sidebar */}
        <div className="sd-sidebar">

          {/* Progression */}
          <div className="sd-card">
            <div className="sd-card__title">Progression</div>
            <div className="od-timeline">
              {order.status === 'cancelled' ? (
                <>
                  <div className="od-timeline-item active">
                    <div className="od-timeline-dot"><i className="material-icons">receipt</i></div>
                    <div className="od-timeline-content">
                      <span className="od-timeline-label">Commande créée</span>
                      <span className="od-timeline-date">{formatDateFR(order.createdAt)}</span>
                    </div>
                  </div>
                  <div className="od-timeline-item cancelled">
                    <div className="od-timeline-dot"><i className="material-icons">cancel</i></div>
                    <div className="od-timeline-content">
                      <span className="od-timeline-label">Annulée</span>
                      <span className="od-timeline-date">{formatDateFR(order.updatedAt)}</span>
                    </div>
                  </div>
                </>
              ) : (
                timelineSteps.map((step) => {
                  const isActive = step.activeStatuses.includes(order.status);
                  return (
                    <div key={step.key} className={`od-timeline-item ${isActive ? 'active' : ''}`}>
                      <div className="od-timeline-dot">
                        <i className="material-icons">{step.icon}</i>
                      </div>
                      <div className="od-timeline-content">
                        <span className="od-timeline-label">{step.label}</span>
                        {isActive && step.key === 'created' && (
                          <span className="od-timeline-date">{formatDateFR(order.createdAt)}</span>
                        )}
                        {isActive && step.key === 'completed' && order.completedAt && (
                          <span className="od-timeline-date">{formatDateFR(order.completedAt)}</span>
                        )}
                        {isActive && step.key === 'sent' && order.sentAt && (
                          <span className="od-timeline-date">{formatDateFR(order.sentAt)}</span>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Soumission */}
          <div className="sd-card">
            <div className="sd-card__title">
              Soumission
              <span className={`status-badge ${order.submission?.status}`} style={{ fontSize: '0.7rem' }}>
                {order.submission?.status === 'pending' && 'En attente'}
                {order.submission?.status === 'approved' && 'Approuvé'}
                {order.submission?.status === 'rejected' && 'Rejeté'}
                {order.submission?.status === 'in_progress' && 'En cours'}
              </span>
            </div>

            <div className="sd-field">
              <div className="sd-label">Titre</div>
              <div className="sd-value sd-value--strong">{order.submission?.name}</div>
            </div>

            {order.submission?.description && (
              <div className="sd-field">
                <div className="sd-label">Description</div>
                <div className="sd-value sd-value--muted">{order.submission.description}</div>
              </div>
            )}

            {order.user && (
              <div className="sd-field">
                <div className="sd-label">Client</div>
                <div className="sd-value">{order.user.firstName} {order.user.lastName}</div>
              </div>
            )}

            <Link
              to={getContextualPath(`submissions/${order.submission?.id}`)}
              className="sd-order-link"
              style={{ marginTop: '0.5rem', display: 'inline-flex' }}
            >
              Voir la soumission
              <i className="material-icons">arrow_forward</i>
            </Link>
          </div>

        </div>

        {/* Middle column — Fichiers */}
        <div className="sd-middle">

          {/* Fichiers */}
          <div className="sd-card">
            <div className="sd-card__title">
              Fichiers
              <span className="sd-badge">
                {order.submission?.files?.length || 0} fichier{(order.submission?.files?.length || 0) > 1 ? 's' : ''}
              </span>
            </div>

            {order.submission?.files?.length > 0 ? (
              <>
                <div className="sd-file-list">
                  {order.submission.files.map(file => (
                    <div
                      key={file.id}
                      className={`sd-file-item ${file.id === activeFileId ? 'active' : ''}`}
                      onClick={() => setActiveFileId(file.id)}
                    >
                      <div className="sd-file-icon">
                        <i className="material-icons">{getFileIcon(file.filename)}</i>
                      </div>
                      <div className="sd-file-info">
                        <div className="sd-file-name">{file.filename}</div>
                        <div className="sd-file-size">{(file.size / (1024 * 1024)).toFixed(2)} MB</div>
                      </div>
                      <button
                        className="sd-file-dl-btn"
                        title="Télécharger"
                        onClick={(e) => { e.stopPropagation(); handleDownload(file.id, file.filename); }}
                        disabled={downloading === file.id}
                      >
                        <i className="material-icons">download</i>
                      </button>
                    </div>
                  ))}
                </div>

                {order.submission.files.length > 1 && (
                  <div className="sd-download-actions">
                    <button
                      className="btn btn-outline-secondary btn-sm"
                      onClick={handleDownloadAll}
                      disabled={downloadingAll || downloadingIndividual}
                    >
                      <i className="material-icons">folder_zip</i>
                      {downloadingAll ? 'Préparation...' : 'Télécharger ZIP'}
                    </button>
                    {/* bouton "Tout télécharger" masqué — fonctionnalité conservée */}
                  </div>
                )}
              </>
            ) : (
              <p className="sd-empty">Aucun fichier</p>
            )}
          </div>

          {/* Notes admin */}
          {order.adminNotes && (
            <div className="sd-card">
              <div className="sd-card__title">Notes administrateur</div>
              <p className="sd-value sd-value--muted">{order.adminNotes}</p>
            </div>
          )}
        </div>

        {/* Right viewer */}
        <div className="sd-viewer">
          <div className="sd-viewer-card">
            <div className="sd-viewer-header">
              <span className="sd-viewer-title">
                {activeFile
                  ? (['stl', 'ply', 'cam'].includes(activeFile.filename.split('.').pop().toLowerCase()) ? 'Aperçu 3D' :
                     activeFile.filename.split('.').pop().toLowerCase() === 'pdf' ? 'Aperçu PDF' : 'Aperçu')
                  : 'Aperçu'}
              </span>
              {activeFile && (
                <span className="sd-viewer-filename">{activeFile.filename}</span>
              )}
            </div>
            <div className="sd-viewer-body">
              {activeFile ? (
                <FileViewer fileId={activeFileId} filename={activeFile.filename} />
              ) : (
                <div className="sd-viewer-empty">
                  <i className="material-icons">view_in_ar</i>
                  <p>Sélectionnez un fichier</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── ZIP progress toast ── */}
      {zipProgress && (
        <div className="zip-progress-toast">
          <div className="zip-progress-toast__header">
            <i className="material-icons">folder_zip</i>
            <span>Préparation du ZIP…</span>
          </div>
          <div className="zip-progress-bar zip-progress-bar--indeterminate">
            <div className="zip-progress-bar__fill zip-progress-bar__fill--indeterminate" />
          </div>
          <div className="zip-progress-toast__info">
            {(zipProgress.loaded / (1024 * 1024)).toFixed(1)} Mo reçus…
          </div>
        </div>
      )}

      {/* ── Modal annulation (utilisateur) ── */}
      {showCancelModal && (
        <div className="modal-overlay">
          <div className="modal-container">
            <div className="modal-header">
              <h3>Confirmer l'annulation</h3>
              <button className="modal-close" onClick={() => setShowCancelModal(false)}>
                <i className="material-icons">close</i>
              </button>
            </div>
            <div className="modal-body">
              <p>Êtes-vous sûr de vouloir annuler cette commande ?</p>
              <p className="text-danger">Cette action est irréversible.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline-secondary" onClick={() => setShowCancelModal(false)}>
                Retour
              </button>
              <button className="btn btn-danger" onClick={handleCancelOrder}>
                Annuler la commande
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal mise à jour statut (admin) ── */}
      {showUpdateModal && (
        <div className="modal-overlay">
          <div className="modal-container">
            <div className="modal-header">
              <h3>Modifier le statut de la commande</h3>
              <button className="modal-close" onClick={() => setShowUpdateModal(false)}>
                <i className="material-icons">close</i>
              </button>
            </div>
            <div className="modal-body">
              <p>
                Passer le statut à <strong>{formatNewStatus(newStatus)}</strong> ?
              </p>
              <div className="form-group">
                <label htmlFor="adminNotes">Notes administrateur (optionnel)</label>
                <textarea
                  id="adminNotes"
                  className="form-control"
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  rows="3"
                  placeholder="Commentaires pour cette mise à jour"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline-secondary" onClick={() => setShowUpdateModal(false)}>
                Annuler
              </button>
              <button className="btn btn-primary" onClick={handleUpdateStatus}>
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderDetails;
